import { FluentProvider, webDarkTheme, webLightTheme } from "@fluentui/react-components";
import * as React from "react";
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { common } from "../../strings";

const commonStr = common();

const THEME_STORAGE_KEY = `${commonStr.appName}-theme`;

type ThemeMode = "Light" | "Dark" | "System";

interface ThemeContextValue {
  themeName: ThemeMode;
  resolvedThemeName: "Light" | "Dark";
  setThemeName: (themeName: ThemeMode) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  themeName: "System",
  resolvedThemeName: "Light",
  setThemeName: (_v: ThemeMode) => {},
  toggleTheme: () => {},
});

export const useTheme = () => useContext(ThemeContext);

// Helper: safe Office access
const hasOffice = (): boolean => {
  try {
    return typeof Office !== "undefined";
  } catch {
    return false;
  }
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Determine initial mode. localstorage is priority; otherwise default to system
  const getInitialMode = (): ThemeMode => {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);

    if (stored === "Light" || stored === "Dark" || stored === "System") return stored;

    return "System";
  };

  const [themeName, setThemeName] = useState<ThemeMode>(getInitialMode);
  const [mqlDark, setMqlDark] = useState<boolean>(
    () => window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ?? false
  );
  const [officeDark, setOfficeDark] = useState<boolean | undefined>(undefined);

  // Resolved theme applied to FluentProvider
  const systemPrefersDark = officeDark ?? mqlDark;
  const resolveThemeName = (): "Light" | "Dark" => {
    if (themeName !== "System") return themeName;
    return systemPrefersDark ? "Dark" : "Light";
  };
  const resolvedThemeName = resolveThemeName();
  const themeObj = resolvedThemeName === "Dark" ? webDarkTheme : webLightTheme;

  // Keep system preference in sync (updates live when OS theme flips)
  useEffect(() => {
    const mql = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mql) return;
    const onChange = (e: MediaQueryListEvent) => setMqlDark(e.matches);
    mql.addEventListener?.("change", onChange);
    mql.addListener?.(onChange); // fallback event listener for older engines
    return () => {
      mql.removeEventListener?.("change", onChange);
      mql.removeListener?.(onChange);
    };
  }, []);

  // Persist and sync document setting for Office
  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, themeName);
    try {
      if (hasOffice() && Office.context?.document?.settings) {
        Office.context.document.settings.set("themeMode", themeName);
        Office.context.document.settings.set("theme", resolvedThemeName);
        Office.context.document.settings.saveAsync?.();
      }
    } catch {
      /* ignore if not available */
    }
  }, [themeName, resolvedThemeName]);

  // Listen for host theme changes.
  useEffect(() => {
    if (!hasOffice()) return;
    try {
      // @ts-ignore - Office.js types may be incomplete
      const canHook = Office.EventType?.OfficeThemeChanged && Office.context?.officeTheme?.addHandlerAsync;

      if (!canHook) return;

      const handler = () => {
        if (themeName === "System") {
          const isDark = Office.context.officeTheme?.isDarkTheme;
          if (typeof isDark === "boolean") {
            setMqlDark(isDark);
          }
        }
      };

      // @ts-ignore - Office.js types may be incomplete
      Office.context.officeTheme.addHandlerAsync(Office.EventType.OfficeThemeChanged, handler);

      return () => {
        try {
          // @ts-ignore - Office.js types may be incomplete
          Office.context.officeTheme.removeHandlerAsync?.(Office.EventType.OfficeThemeChanged, { handler });
        } catch (err) {
          console.error("Error removing office theme handler:", err);
        }
      };
    } catch (err) {
      console.error("Error adding office theme handler:", err);
    }
  }, [themeName]);

  // Word-specific: word doesn't have a theme-change event to subscribe to. We need
  // to occasionally poll to see if changes occurred.
  useEffect(() => {
    // Only do this when following System mode
    if (themeName !== "System") return;
    if (!hasOffice()) return;

    const readOfficeDark = () => {
      try {
        const v = Office.context?.officeTheme?.isDarkTheme;
        if (typeof v === "boolean") setOfficeDark(v);
      } catch (err) {
        console.error("Error reading office theme:", err);
      }
    };

    // Update when task pane becomes visible or focused
    const onVis = () => {
      if (document.visibilityState === "visible") readOfficeDark();
    };
    const onFocus = () => readOfficeDark();

    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onFocus);

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") readOfficeDark();
    }, 2000);

    readOfficeDark();

    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onFocus);
      clearInterval(intervalId);
    };
  }, [themeName]);

  const toggleTheme = () => {
    const order: ThemeMode[] = ["Light", "Dark", "System"];
    setThemeName((t) => order[(order.indexOf(t) + 1) % order.length]);
  };

  const value = useMemo<ThemeContextValue>(
    () => ({ themeName, resolvedThemeName, setThemeName, toggleTheme }),
    [themeName, resolvedThemeName]
  );

  return (
    <ThemeContext.Provider value={value}>
      <FluentProvider style={{ height: "100%" }} theme={themeObj} id="5">
        {children}
      </FluentProvider>
    </ThemeContext.Provider>
  );
};
